<template>
  <div>
    <v-app-bar
      dense
      flat
      height="56"
    >
      <v-text-field
        id="search_field"
        v-model.trim="searchText"
        append-icon="mdi-magnify"
        label="Search"
        single-line
        clearable
        outlined
        hide-details
        dense
        class="migration-search"
      />
      <v-spacer />
      <v-btn
        id="migration_export"
        color="primary"
        class="ml-3 elevation-0"
        :disabled="!rows.length"
        @click="download()"
      >
        <v-icon>mdi-cloud-download</v-icon>
      </v-btn>
      <v-btn
        id="menu_item_refresh"
        color="primary"
        :loading="loader"
        class="ml-2 elevation-0"
        @click.native="getSummary()"
      >
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-app-bar>

    <v-card
      flat
      outlined
      tile
    >
      <v-card-text>
        <v-tabs
          v-model="tab"
          class="mb-4"
        >
          <v-tab>Accounts</v-tab>
          <v-tab>Corporates</v-tab>
        </v-tabs>

        <v-row dense>
          <v-col
            v-for="card in cards"
            :key="`card--${card.key}`"
            cols="6"
            sm="3"
          >
            <v-card
              outlined
              :ripple="false"
              :class="statusFilter === card.key ? `${card.color} lighten-5` : ''"
              @click="toggleFilter(card.key)"
            >
              <v-card-text class="py-2 px-3 text-center">
                <div
                  class="text-h6 font-weight-medium"
                  :class="`${card.color}--text`"
                >
                  {{ card.count }}
                </div>
                <div class="caption grey--text text--darken-1 text-truncate">
                  {{ card.label }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <div class="mt-4 mb-1 d-flex align-center flex-wrap">
          <span class="text-body-2 grey--text text--darken-2">
            {{ migrated }} of {{ totalUsers }} users are on the new login
          </span>
          <v-spacer />
          <span class="text-body-2 font-weight-medium">{{ percentLabel }}</span>
        </div>
        <v-progress-linear
          :value="percent"
          height="8"
          rounded
          color="success"
          background-color="grey lighten-3"
        />

        <div class="mt-4 d-flex align-center">
          <span class="caption grey--text">
            Click any row to see that {{ entityLabel.toLowerCase() }}'s users.
          </span>
          <v-spacer />
          <v-btn
            v-if="statusFilter"
            small
            text
            color="primary"
            @click="statusFilter = null"
          >
            Clear filter
          </v-btn>
        </div>

        <v-skeleton-loader
          v-if="initialLoading"
          type="table-thead, table-tbody"
        />
        <v-data-table
          v-else
          :headers="headers"
          :items="rows"
          :loading="loader"
          :items-per-page="15"
          :footer-props="{ 'items-per-page-options': [15, 50, 100, -1] }"
          class="elevation-0"
          @click:row="openUsers"
        >
          <template v-slot:item.name="{ item }">
            <span class="font-weight-medium">{{ item.name }}</span>
          </template>

          <template v-slot:item.oldLogin="{ item }">
            <span :class="{ 'grey--text text--lighten-1': !item.oldLogin }">
              {{ item.oldLogin }}
            </span>
          </template>

          <template v-slot:item.pending="{ item }">
            <span :class="{ 'grey--text text--lighten-1': !item.pending }">
              {{ item.pending }}
            </span>
          </template>

          <template v-slot:item.newLogin="{ item }">
            <span :class="{ 'grey--text text--lighten-1': !item.newLogin }">
              {{ item.newLogin }}
            </span>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip
              small
              label
              :color="statusOf(item).color"
              text-color="white"
              class="status-chip"
            >
              {{ statusOf(item).label }}
            </v-chip>
          </template>

          <template v-slot:no-data>
            <div class="py-8 grey--text">
              Nothing to show
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <migration-users
      v-if="selected"
      :show-dialog.sync="showUsers"
      :scope="selected.scope"
      :entity-id="selected.id"
      :name="selected.name"
    />
  </div>
</template>

<script>
import { failure, csvDownload } from "@/mixins/common";
import MigrationUsers from "./MigrationUsers";

const STATUSES = {
  notStarted: { label: "Not started", color: "error" },
  inProgress: { label: "In progress", color: "warning" },
  done: { label: "Done", color: "success" },
  noUsers: { label: "No users", color: "grey" }
};

export default {
  name: "MigrationDashboard",
  components: {
    MigrationUsers
  },
  mixins: [failure, csvDownload],
  data() {
    return {
      loader: false,
      loaded: false,
      tab: 0,
      accounts: [],
      corporates: [],
      searchText: null,
      statusFilter: null,
      showUsers: false,
      selected: null
    };
  },
  computed: {
    initialLoading() {
      return this.loader && !this.loaded;
    },
    entityLabel() {
      return this.tab === 0 ? "Account" : "Corporate";
    },
    section() {
      return this.tab === 0 ? "accounts" : "corporates";
    },
    headers() {
      return [
        { text: this.entityLabel, value: "name" },
        { text: "Total users", value: "totalUsers", align: "end" },
        { text: "Old login", value: "oldLogin", align: "end" },
        { text: "Invited", value: "pending", align: "end" },
        { text: "New login", value: "newLogin", align: "end" },
        { text: "Status", value: "status", sortable: false, align: "end" }
      ];
    },
    tabSource() {
      return this.tab === 0 ? this.accounts : this.corporates;
    },
    cards() {
      return Object.keys(STATUSES).map((key) => ({
        key,
        label: STATUSES[key].label,
        color: STATUSES[key].color,
        count: this.tabSource.filter((row) => this.statusKey(row) === key).length
      }));
    },
    rows() {
      let list = this.tabSource;
      if (this.statusFilter) {
        list = list.filter((row) => this.statusKey(row) === this.statusFilter);
      }
      if (this.searchText) {
        const needle = this.searchText.toLowerCase();
        list = list.filter((row) => row.name.toLowerCase().includes(needle));
      }
      return list;
    },
    totalUsers() {
      return this.tabSource.reduce((sum, row) => sum + row.totalUsers, 0);
    },
    migrated() {
      return this.tabSource.reduce((sum, row) => sum + row.newLogin, 0);
    },
    percent() {
      return this.totalUsers ? (this.migrated / this.totalUsers) * 100 : 0;
    },
    percentLabel() {
      return `${Math.round(this.percent)}%`;
    }
  },
  created() {
    this.$store.commit("setTitle", "Login Migration");
    this.getSummary();
  },
  methods: {
    // accounts and corporates are independent resources on separate routes, so
    // they are fetched separately and neither blocks the other
    getSummary() {
      this.loader = true;
      // settles on both paths: handleFailure reads failure.body.message, which
      // throws when the response has no body (server down), and that rejection
      // would otherwise leave the loader stuck on
      const done = () => {
        this.loader = false;
        this.loaded = true;
      };
      Promise.all([
        this.fetchSection("accounts"),
        this.fetchSection("corporates")
      ]).then(done, done);
    },
    fetchSection(section) {
      return this.$http.get(`${this.ADMIN_API_URL}/sso/migration/${section}`).then(
        (res) => {
          this[section] = res.body || [];
        },
        (failure) => {
          this[section] = [];
          this.handleFailure(failure);
        }
      );
    },
    statusKey(row) {
      if (!row.totalUsers) {
        return "noUsers";
      }
      if (row.newLogin === row.totalUsers) {
        return "done";
      }
      if (row.oldLogin === row.totalUsers) {
        return "notStarted";
      }
      return "inProgress";
    },
    statusOf(row) {
      return STATUSES[this.statusKey(row)];
    },
    toggleFilter(key) {
      this.statusFilter = this.statusFilter === key ? null : key;
    },
    openUsers(row) {
      this.selected = { scope: this.section, id: row.id, name: row.name };
      this.showUsers = true;
    },
    download() {
      this.downloadCsv(
        `login_migration_${this.section}`,
        [this.entityLabel, "Total users", "Old login", "Invited", "New login", "Status"],
        this.rows.map((row) => [
          row.name,
          row.totalUsers,
          row.oldLogin,
          row.pending,
          row.newLogin,
          this.statusOf(row).label
        ])
      );
    }
  }
};
</script>

<style scoped>
::v-deep tbody tr {
  cursor: pointer;
}

/* the app bar is a flex row, so the field would otherwise stretch across it */
.migration-search {
  max-width: 320px;
}

/* fixed width so the labels don't leave a ragged edge down the column */
.status-chip {
  min-width: 104px;
  justify-content: center;
}

/* the theme defines a colour named "active", which generates a global
   background class; v-data-table puts "active" on the sorted th, so the
   header would otherwise fill solid orange */
::v-deep .v-data-table th.active {
  background-color: transparent !important;
}
</style>
